import { useIntl } from "react-intl";
import { useBudgetDetails, IUseBudgetDetailsOverrides } from "./hooks/useBudgetDetails";

import { BudgetVehicleCard } from "../components/BudgetVehicleCard";
import { Service } from "./components/Service";
import { findProposalService } from "./utils/findProposalService";
import { isPreviousVersion } from "./utils/isPreviousVersion";
import { ServiceSkeleton } from "./components/ServiceSkeleton";
import { BudgetHeader } from "../components/BudgetHeader";
import { BudgetSummary } from "../components/BudgetSummary";
import { BudgetAttachments } from "../components/budgetAttachments";
import { CounterProposal } from "../budgetEdit/components/CounterProposal";

import "../index.css";

const SkeletonBlock = ({ className = "" }: { className?: string }) => (
    <div className={`animate-pulse rounded bg-gray-300 ${className}`} />
);

interface IBudgetDetailsProps extends IUseBudgetDetailsOverrides {
    persona: string;
    onClose?: () => void;
    onBreadcrumbClick?: () => void;
}

const BudgetDetails = (props: IBudgetDetailsProps) => {
    const intl = useIntl();

    const { persona, onClose, onBreadcrumbClick, ...overrides } = props;

    const { budget, truckerPayPlan, loading, attachments } = useBudgetDetails(persona, overrides);

    return (
        <div className="w-full">
            <BudgetHeader persona={persona} budget={budget} loading={loading} onClose={onClose} onBreadcrumbClick={onBreadcrumbClick} />

            {<BudgetVehicleCard budget={budget} loading={loading || !budget} />}

            {!loading ? (
                <div className={`mb-6 px-6`}>
                    <div className={`font-semibold mb-4 text-size-18`}>{intl.formatMessage({ id: 'intl-msg:budgets.values' })}</div>
                    {(budget?.versionData?.services ?? []).map((service, index) => (
                        <Service
                            key={service.id || `service-${index}`}
                            service={service}
                            proposalService={isPreviousVersion(budget) ? findProposalService(budget?.versionData?.proposalServices, service) : undefined}
                            serviceIndex={index}
                        />
                    ))}
                </div>
            ) : <ServiceSkeleton />}

            {!loading ? <BudgetSummary persona={persona} budget={budget} truckerPayPlan={truckerPayPlan} /> : <SkeletonBlock className="h-[100px] w-full mb-4" />}

            {!loading && budget && (Boolean(budget.versionData?.counterProposalValue) || Boolean(budget.versionData?.counterProposalObservation?.trim())) && (
                <CounterProposal budget={budget} readOnly={true} />
            )}

            {!loading && (
                <BudgetAttachments
                    files={attachments}
                    budgetId={budget?.uuid}
                    allowUpload={false}
                />
            )}
        </div>
    );
};

export default BudgetDetails;